// Quien te necesita y queda fuera de la vista (arriba o abajo): un botón en el borde que lleva hasta su mesa (como
// Gather). Escucha el desplazamiento él solo: la escena no se vuelve a pintar en cada fotograma de scroll.
import { ArrowDown, ArrowUp } from 'lucide-react';
import { memo, useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import { t } from '../../../i18n';
import type { MesaMundo, Mundo } from './generar';

export const IndicadoresFuera = memo(function IndicadoresFuera({
  caja,
  mundo,
  destacadas,
  alto,
  centroX,
  conBarra,
}: {
  /** La vista que se desplaza. */
  caja: RefObject<HTMLDivElement>;
  mundo: Mundo | null;
  /** Las mesas de quien te necesita, en el orden del mundo. */
  destacadas: readonly MesaMundo[];
  /** Alto del hueco de la oficina en pantalla. */
  alto: number;
  centroX: number;
  /** La barra de «Ordenar» ocupa el borde de abajo: el indicador de abajo sube por encima. */
  conBarra: boolean;
}) {
  const [vistaY, setVistaY] = useState(0);
  useLayoutEffect(() => {
    if (caja.current) setVistaY(caja.current.scrollTop);
  }, [caja, mundo]);
  useEffect(() => {
    const el = caja.current;
    if (!el) return;
    let raf = 0;
    const alMover = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setVistaY(el.scrollTop));
    };
    el.addEventListener('scroll', alMover, { passive: true });
    return () => {
      el.removeEventListener('scroll', alMover);
      cancelAnimationFrame(raf);
    };
  }, [caja]);

  if (!mundo || !alto) return null;
  const fuera = { arriba: [] as MesaMundo[], abajo: [] as MesaMundo[] };
  for (const me of destacadas) {
    const y0 = me.rect.y * mundo.z - vistaY;
    if (y0 + me.rect.h * mundo.z * 0.5 < 0) fuera.arriba.push(me);
    else if (y0 + me.rect.h * mundo.z * 0.5 > alto) fuera.abajo.push(me);
  }
  const irA = (me: MesaMundo) => caja.current?.scrollTo({ top: Math.max(0, (me.rect.y + me.rect.h / 2) * mundo.z - caja.current.clientHeight / 2), behavior: 'smooth' });
  return (
    <>
      {(['arriba', 'abajo'] as const).map((lado) =>
        fuera[lado].length ? (
          <button
            key={lado}
            type="button"
            onClick={() => irA(fuera[lado][0]!)}
            className="absolute z-40 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-oficina-acento px-3.5 py-1.5 text-o-texto font-semibold text-oficina-acento-tinta shadow-o-hoja transition-colors hover:bg-oficina-acento-hover"
            style={{ left: centroX, [lado === 'arriba' ? 'top' : 'bottom']: lado === 'abajo' && conBarra ? 84 : 16 }}
          >
            {lado === 'arriba' ? <ArrowUp className="h-4 w-4" aria-hidden /> : <ArrowDown className="h-4 w-4" aria-hidden />}
            {t(fuera[lado].length === 1 ? 'oficinaV2.resumen.teNecesitaUno' : 'oficinaV2.resumen.teNecesitanVarios', { n: fuera[lado].length })}
          </button>
        ) : null,
      )}
    </>
  );
});
